import { t } from '@lingui/core/macro';
import { Box, IconButton, Stack, styled } from '@mui/material';
import {
    addYears,
    endOfMonth,
    endOfQuarter,
    endOfYear,
    format,
    isSameDay,
    startOfMonth,
    startOfQuarter,
    startOfYear,
} from 'date-fns';
import { de as deLocale } from 'date-fns/locale/de';
import { enUS as enLocale } from 'date-fns/locale/en-US';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import * as React from 'react';
import type { JSX } from 'react';

import { bodySmall, bodySmallSemiBold, colorSystem } from '../../../../theme';
import type { DateLocale } from './localization';

export interface Period {
    from: Date;
    to: Date;
}

export interface PeriodGridProps {
    /** The "anchor" date that controls which year(s) are shown. */
    referenceDate: Date;
    onReferenceDateChange: (next: Date) => void;
    /** Currently selected date (so we can highlight the matching period cell). */
    selected?: Date | null;
    onPick: (period: Period) => void;
    minDate?: Date;
    maxDate?: Date;
    locale: DateLocale;
}

export function MonthGrid(props: PeriodGridProps): JSX.Element {
    const { referenceDate, onReferenceDateChange, locale } = props;
    const dfns = locale === 'de' ? deLocale : enLocale;
    const year = referenceDate.getFullYear();

    return (
        <GridRoot>
            <SectionHeader
                label={String(year)}
                onPrev={() => onReferenceDateChange(addYears(referenceDate, -1))}
                onNext={() => onReferenceDateChange(addYears(referenceDate, 1))}
            />
            <CellGrid columns={4}>
                {Array.from({ length: 12 }, (_, i) => {
                    const start = startOfMonth(new Date(year, i, 1));
                    const end = endOfMonth(start);
                    return (
                        <PeriodCell
                            key={i}
                            label={format(start, 'LLL', { locale: dfns })}
                            start={start}
                            end={end}
                            {...props}
                        />
                    );
                })}
            </CellGrid>
        </GridRoot>
    );
}

export function QuarterGrid(props: PeriodGridProps): JSX.Element {
    return <YearGroupedGrid {...props} columns={4} cells={(year) => quarterCells(year)} />;
}

export function YearGrid(props: PeriodGridProps): JSX.Element {
    const { referenceDate, onReferenceDateChange } = props;
    const baseYear = referenceDate.getFullYear();
    // Nine years from the reference year, paged by nine, so year N and N+9 share a cell.
    const years = Array.from({ length: 9 }, (_, i) => baseYear + i);

    return (
        <GridRoot>
            <SectionHeader
                label={`${years[0]} – ${years[years.length - 1]}`}
                onPrev={() => onReferenceDateChange(addYears(referenceDate, -9))}
                onNext={() => onReferenceDateChange(addYears(referenceDate, 9))}
            />
            <CellGrid columns={3}>
                {years.map((y) => {
                    const start = startOfYear(new Date(y, 0, 1));
                    const end = endOfYear(start);
                    return <PeriodCell key={y} label={String(y)} start={start} end={end} {...props} />;
                })}
            </CellGrid>
        </GridRoot>
    );
}

interface YearGroupedGridProps extends PeriodGridProps {
    columns: number;
    cells: (year: number) => Array<{ label: string; start: Date; end: Date; span?: number }>;
}

function YearGroupedGrid(props: YearGroupedGridProps) {
    const { referenceDate, onReferenceDateChange, columns, cells, ...rest } = props;
    const baseYear = referenceDate.getFullYear();
    const years = [baseYear, baseYear + 1];

    return (
        <GridRoot>
            <SectionHeader
                onPrev={() => onReferenceDateChange(addYears(referenceDate, -1))}
                onNext={() => onReferenceDateChange(addYears(referenceDate, 1))}
                label={`${years[0]} – ${years[1]}`}
            />
            <Stack spacing="12px">
                {years.map((year) => (
                    <Stack key={year} spacing="6px">
                        <YearLabel>{year}</YearLabel>
                        <CellGrid columns={columns}>
                            {cells(year).map((c) => (
                                <PeriodCell
                                    key={c.label}
                                    label={c.label}
                                    start={c.start}
                                    end={c.end}
                                    span={c.span}
                                    referenceDate={referenceDate}
                                    onReferenceDateChange={onReferenceDateChange}
                                    {...rest}
                                />
                            ))}
                        </CellGrid>
                    </Stack>
                ))}
            </Stack>
        </GridRoot>
    );
}

function quarterCells(year: number): Array<{ label: string; start: Date; end: Date }> {
    return [0, 1, 2, 3].map((q) => {
        const start = startOfQuarter(new Date(year, q * 3, 1));
        return { label: `Q${q + 1}`, start, end: endOfQuarter(start) };
    });
}

interface PeriodCellProps extends PeriodGridProps {
    label: string;
    start: Date;
    end: Date;
    span?: number;
}

function PeriodCell({ label, start, end, selected, onPick, minDate, maxDate, span }: PeriodCellProps) {
    const disabled = Boolean((maxDate && start > maxDate) || (minDate && end < minDate));
    const isSelected = isPeriodSelected(start, end, selected);

    return (
        <Cell
            type="button"
            disabled={disabled}
            data-selected={isSelected || undefined}
            // The exact range on hover tells start- and end-anchored period clicks apart.
            title={`${formatRangeTitle(start, end)}`}
            onClick={() => onPick({ from: start, to: end })}
            style={span ? { gridColumn: `span ${span}` } : undefined}
        >
            {label}
        </Cell>
    );
}

function isPeriodSelected(start: Date, end: Date, selected?: Date | null): boolean {
    if (!selected) return false;
    // A period commits its start or its end, depending on the anchor, so either matches.
    return isSameDay(start, selected) || isSameDay(end, selected);
}

function formatRangeTitle(start: Date, end: Date): string {
    return `${format(start, 'PP')} – ${format(end, 'PP')}`;
}

function SectionHeader({ label, onPrev, onNext }: { label?: string; onPrev: () => void; onNext: () => void }) {
    return (
        <Stack
            direction="row"
            sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <IconButton size="small" aria-label={t`Previous`} onClick={onPrev}>
                <ChevronLeftIcon size={20} />
            </IconButton>
            {label ? <HeaderLabel>{label}</HeaderLabel> : <Box />}
            <IconButton size="small" aria-label={t`Next`} onClick={onNext}>
                <ChevronRightIcon size={20} />
            </IconButton>
        </Stack>
    );
}

const GridRoot = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 8px 12px',
    gap: '8px',
});

const HeaderLabel = styled('div')({
    ...bodySmallSemiBold,
    color: colorSystem.neutral[8],
});

const YearLabel = styled('div')({
    ...bodySmall,
    color: colorSystem.neutral[6],
    paddingLeft: '4px',
});

const CellGrid = styled('div', { shouldForwardProp: (p) => p !== 'columns' })<{ columns: number }>(({ columns }) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, 1fr)`,
    gap: '6px',
}));

const Cell = styled('button')({
    ...bodySmallSemiBold,
    height: '32px',
    padding: '0 8px',
    borderRadius: '6px',
    border: `1px solid ${colorSystem.neutral[2]}`,
    background: colorSystem.neutral.white,
    color: colorSystem.neutral[8],
    cursor: 'pointer',
    transition: 'background 120ms ease, border-color 120ms ease, color 120ms ease',
    '&:hover:not(:disabled)': {
        background: colorSystem.primary[1],
        borderColor: colorSystem.primary[3],
        color: colorSystem.primary[7],
    },
    '&[data-selected]': {
        background: colorSystem.primary[2],
        borderColor: colorSystem.primary[5],
        color: colorSystem.primary[8],
    },
    '&:disabled': {
        background: colorSystem.neutral[1],
        borderColor: colorSystem.neutral[2],
        color: colorSystem.neutral[5],
        cursor: 'not-allowed',
    },
});
